import React from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import UserDirectoryToolbar from '../components/users/UserDirectoryToolbar';
import { useUserDirectory } from '../hooks/useUserDirectory';
import ToastBanner from '../components/ToastBanner';
import XmitCell from '../components/users/XmitCell';
import { formatRelativeDate } from '../utils/userDirectoryFilters';

function getInitials(firstName, lastName) {
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  if (!name) return '??';
  return name.split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase();
}

function VerificationBadge({ status }) {
  const map = {
    fully_verified: { label: 'Verified', bg: '#DCFCE7', color: '#15803D' },
    email_verified: { label: 'Email', bg: '#DBEAFE', color: '#1D4ED8' },
    mobile_verified: { label: 'Mobile', bg: '#DBEAFE', color: '#1D4ED8' },
    unverified: { label: 'Unverified', bg: '#FEF3C7', color: '#B45309' },
  };
  const cfg = map[status] || { label: status || 'Unknown', bg: '#F1F5F9', color: '#64748B' };
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.color,
        padding: '3px 9px',
        borderRadius: 99,
      }}
    >
      {cfg.label}
    </span>
  );
}

const exportBtnStyle = {
  background: '#F1F5F9',
  color: '#475569',
  border: '1px solid #E2E8F0',
  padding: '8px 14px',
  fontSize: 12,
};

function ActivityCell({ activity }) {
  if (!activity) return <span style={{ color: '#94A3B8', fontSize: 12 }}>Never</span>;
  if (activity.label === 'Today') {
    return (
      <span style={{ color: '#2E7D32', fontWeight: 600, fontSize: 12 }}>
        <i className="fas fa-circle" style={{ fontSize: 7, marginRight: 3 }} />
        Today
      </span>
    );
  }
  if (activity.label === 'Never') {
    return <span style={{ color: '#94A3B8', fontSize: 12 }}>Never</span>;
  }
  return <span style={{ color: '#94A3B8', fontSize: 12 }}>{activity.label} ago</span>;
}

export default function OnboardingUsers() {
  const navigate = useNavigate();
  const {
    users,
    filters,
    filterOptions,
    error,
    toast,
    loading,
    paginationProps,
    applyFilters,
    handleFilterChange,
    handleSearchChange,
    resetFilters,
    handleExport,
  } = useUserDirectory({
    onboardingStatus: 'incomplete',
    exportFilename: 'onboarding-users-export.csv',
  });

  return (
    <>
      <ToastBanner toast={toast} />

      <PageHeader
        inline
        trail={[{ label: 'Users', to: '/users' }, { label: 'Onboarding Progress' }]}
        actions={
          <button
            type="button"
            className="action-btn"
            style={exportBtnStyle}
            onClick={handleExport}
            disabled={loading}
          >
            <i className="fas fa-download" style={{ fontSize: 11 }} />
            Export
          </button>
        }
      />

      {error && (
        <div
          className="fade-up"
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 10,
            color: '#B91C1C',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div className="fade-up delay-1">
        <div className="section-card">
          <UserDirectoryToolbar
            filters={filters}
            filterOptions={filterOptions}
            variant="incomplete"
            loading={loading}
            onFilterChange={handleFilterChange}
            onSearchChange={handleSearchChange}
            onApply={applyFilters}
            onReset={resetFilters}
          />

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 800 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>XMIT</th>
                  <th>Current Step</th>
                  <th>Progress</th>
                  <th>Verification</th>
                  <th>Signed up</th>
                  <th>Activity</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>
                      Loading users…
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>
                      No users match your filters.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr
                      key={u.id}
                      className="user-row"
                      onClick={() => navigate(`/users/${u.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              background: 'linear-gradient(135deg,#102A43,#074f78)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#fff',
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(u.firstName, u.lastName)}
                          </div>
                          <span style={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>
                            {u.firstName} {u.lastName}
                          </span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#64748B' }}>{u.email || '—'}</td>
                      <td style={{ fontSize: 12, color: '#64748B', fontFamily: "'DM Mono',monospace" }}>
                        {u.mobile || '—'}
                      </td>
                      <td>
                        <XmitCell quickResponse={u.quickResponse} />
                      </td>
                      <td style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>
                        {u.onboarding?.currentStepLabel || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748B' }}>
                        {u.onboarding?.progressLabel || '—'}
                      </td>
                      <td>
                        <VerificationBadge status={u.verificationStatus} />
                      </td>
                      <td style={{ fontSize: 12, color: '#64748B' }}>
                        {formatRelativeDate(u.createdAt)}
                      </td>
                      <td>
                        <ActivityCell activity={u.activity} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination {...paginationProps} />
      </div>
    </>
  );
}
